import { Controller } from "@hotwired/stimulus";

// Captures a point for a street-outreach workflow step. Unlike
// street_outreach_map_controller (which AJAX-posts to a persisted record), the
// runtime workflow form has no persisted StreetOutreach yet, so this controller
// writes the chosen lat/lng into hidden form fields that submit with the step.
//
// Point can be set by geolocating (on connect / "Use my location") or, when GPS
// is unavailable, by clicking the map or dragging the pin (the no-GPS fallback).
// Degrades gracefully: if Google Maps or geolocation is missing, the form still
// submits and the server blocks completion when no point was captured.
export default class extends Controller {
  static targets = ["map", "latitude", "longitude", "status", "placesInput"];

  static values = {
    autoLocate: { type: Boolean, default: true },
    defaultLat: { type: Number, default: 39.8283 },
    defaultLng: { type: Number, default: -98.5795 },
  };

  connect() {
    this.active = true;
    if (this.autoLocateValue && !this.hasCurrentPoint()) this.requestGeolocation();

    if (window.google?.maps) {
      this.initializeGoogle();
    } else {
      this.boundInit = () => this.initializeGoogle();
      window.addEventListener("google-maps-loaded", this.boundInit, {
        once: true,
      });
    }
  }

  initializeGoogle() {
    this.initializeMap();
    this.initializeAutocomplete();
  }

  disconnect() {
    this.active = false;
    if (this.boundInit) {
      window.removeEventListener("google-maps-loaded", this.boundInit);
    }
  }

  initializeMap() {
    if (!this.hasMapTarget || !window.google?.maps) return;

    const hasPoint = this.currentLat() !== null && this.currentLng() !== null;
    const center = hasPoint
      ? { lat: this.currentLat(), lng: this.currentLng() }
      : { lat: this.defaultLatValue, lng: this.defaultLngValue };

    this.map = new google.maps.Map(this.mapTarget, {
      center,
      zoom: hasPoint ? 16 : 4,
      disableDefaultUI: true,
      zoomControl: true,
    });

    this.marker = new google.maps.Marker({
      map: this.map,
      position: center,
      draggable: true,
      visible: hasPoint,
    });

    // Drop a pin where the worker taps (no-GPS fallback).
    this.map.addListener("click", (event) => {
      this.setPoint(event.latLng.lat(), event.latLng.lng());
    });

    // Dragging the pin refines the point.
    this.marker.addListener("dragend", (event) => {
      this.setPoint(event.latLng.lat(), event.latLng.lng());
    });

    if (hasPoint) {
      this.setStatus(this.statusMessage("set"));
    }
  }

  // Places search box (same picker as the interaction modal): selecting a
  // place moves the pin and fills the hidden lat/lng fields.
  initializeAutocomplete() {
    if (!this.hasPlacesInputTarget || !window.google?.maps?.places) return;

    this.autocomplete = new google.maps.places.Autocomplete(this.placesInputTarget, {
      fields: ["geometry", "name", "formatted_address"],
      types: ["geocode", "establishment"],
    });

    // Prevent Enter in the search box from submitting the step form.
    this.placesInputTarget.addEventListener("keydown", (event) => {
      if (event.key === "Enter") event.preventDefault();
    });

    this.autocomplete.addListener("place_changed", () => {
      const place = this.autocomplete.getPlace();
      if (!place.geometry?.location) return;

      const label = place.name || place.formatted_address || this.placesInputTarget.value;
      this.setPoint(place.geometry.location.lat(), place.geometry.location.lng(), 16);
      this.placesInputTarget.value = label;
    });
  }

  // Action: "Use my location" button.
  useMyLocation() {
    this.requestGeolocation();
  }

  requestGeolocation() {
    if (!("geolocation" in navigator)) {
      this.setStatus(this.statusMessage("unavailable"));
      return;
    }

    this.setStatus(this.statusMessage("locating"));
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (this.active) this.setPoint(position.coords.latitude, position.coords.longitude, 16);
      },
      () => {
        if (this.active) this.setStatus(this.statusMessage("unavailable"));
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 },
    );
  }

  setPoint(lat, lng, zoom) {
    if (this.hasPlacesInputTarget) this.placesInputTarget.value = "";
    if (this.hasLatitudeTarget) this.latitudeTarget.value = String(lat);
    if (this.hasLongitudeTarget) this.longitudeTarget.value = String(lng);

    if (this.map && this.marker) {
      const position = { lat, lng };
      this.marker.setPosition(position);
      this.marker.setVisible(true);
      this.map.setCenter(position);
      if (zoom) this.map.setZoom(zoom);
    }

    this.setStatus(this.statusMessage("set"));
  }

  placeChanged() {
    if (this.hasLatitudeTarget) this.latitudeTarget.value = "";
    if (this.hasLongitudeTarget) this.longitudeTarget.value = "";
  }

  currentLat() {
    return this.parseCoord(this.hasLatitudeTarget ? this.latitudeTarget.value : "");
  }

  currentLng() {
    return this.parseCoord(this.hasLongitudeTarget ? this.longitudeTarget.value : "");
  }

  hasCurrentPoint() {
    return this.currentLat() !== null && this.currentLng() !== null;
  }

  parseCoord(value) {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  setStatus(message) {
    if (this.hasStatusTarget && message) this.statusTarget.textContent = message;
  }

  // Localized status copy is supplied via data attributes so the partial owns
  // i18n: data-outreach-location-status-<key>="...".
  statusMessage(key) {
    return this.data.get(`status-${key}`) || "";
  }
}
